import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { AppleButton } from "./apple-button";
import { ArrowRight, X } from "lucide-react";

interface RegisterAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  assetId: string;
  scanType: 'NFC' | 'QR';
  format?: string;
  isPublic?: boolean;
  onRegister?: () => void;
}

const RegisterAssetModal = ({ 
  isOpen, 
  onClose, 
  assetId, 
  scanType,
  format,
  isPublic = true,
  onRegister
}: RegisterAssetModalProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleAction = () => {
    // If we're in the public scan page, redirect to auth
    if (location.pathname === "/public/scan") {
      navigate("/auth");
    } else if (onRegister) {
      // For /scan page or other authenticated pages, show create form
      onRegister();
    }
  };

  if (!isOpen) return null;

  const isPublicScanPage = location.pathname === "/public/scan";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 relative"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto">
            <div className="w-8 h-8 text-blue-500">
              {scanType === 'NFC' ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a10 10 0 0 1 10 10 10 10 0 0 1-10 10A10 10 0 0 1 2 12" />
                  <path d="M12 12 8 8" />
                  <path d="m12 12 4 4" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M7 7h.01" />
                  <path d="M17 7h.01" />
                  <path d="M7 17h.01" />
                  <path d="M17 17h.01" />
                </svg>
              )}
            </div>
          </div>

          <h3 className="text-xl font-semibold text-gray-900">
            Unregistered {scanType} Tag
          </h3>

          <div className="space-y-2">
            <p className="text-gray-600">
              This {format || scanType} code ({assetId}) is not registered in our system.
            </p>
            <p className="text-gray-600">
              {isPublicScanPage 
                ? "Would you like to register and manage this asset?"
                : "Would you like to create a new asset with this tag?"}
            </p>
          </div>

          <div className="pt-6 space-y-3">
            <AppleButton
              onClick={handleAction}
              className="w-full justify-center py-3"
            >
              {isPublicScanPage ? "Register Asset" : "Create Asset"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </AppleButton>
            <button
              onClick={onClose}
              className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterAssetModal; 